import apiRequest from './apiClient';

/**
 * Загрузка списка событий с cursor-based пагинацией.
 *
 * @param {'initial'|'before'|'after'} direction
 * @param {string|null} cursor  — курсор из предыдущего ответа
 * @returns {Promise<EventListResponse>}
 */
/**
 * @param {'initial'|'before'|'after'} direction
 * @param {string|null} cursor
 * @param {number|null} chatId  — null = без per-chat данных; 0 = личный чат; >0 = группа
 */
export async function getEvents({ direction = 'initial', cursor = null, chatId = null } = {}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const params = new URLSearchParams({ timezone });
  if (direction === 'before' && cursor) params.set('before', cursor);
  if (direction === 'after'  && cursor) params.set('after',  cursor);
  if (chatId !== null && chatId !== undefined) params.set('chatId', String(chatId));

  return apiRequest(`/api/events?${params}`);
}

/**
 * Создать событие
 * POST /api/events
 * @param {boolean} publishDirectly — true: создать и опубликовать; false: только сохранить
 */
export const createEvent = (data, publishDirectly = true) =>
  apiRequest(`/api/events?publishDirectly=${publishDirectly}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * Создать событие в чистом inline-режиме (auth через hash, без публикации).
 * Фронт после создания вызывает tg.switchInlineQuery, возвращая publicId в чат.
 * POST /api/events?userId=123&hash=abc
 */
export const createEventInline = (data, userId, hash) =>
  apiRequest(`/api/events?userId=${userId}&hash=${hash}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * Создать событие из inline-режима, но с прямой публикацией в выбранный чат.
 * Используется когда пользователь в inline-режиме переключился на ручной выбор чата.
 * X-Telegram-Init-Data недоступен, поэтому auth по-прежнему через userId+hash,
 * но publishDirectly=true говорит бэкенду опубликовать сообщение самостоятельно.
 * POST /api/events?userId=123&hash=abc&publishDirectly=true
 */
export const createEventInlineOverride = (data, userId, hash) =>
  apiRequest(`/api/events?userId=${userId}&hash=${hash}&publishDirectly=true`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * Получить событие по ID
 * GET /api/events/{eventId}
 */
export const getEvent = (eventId) =>
  apiRequest(`/api/events/${eventId}`);

/**
 * Обновить событие
 * PUT /api/events/{eventId}
 */
export const updateEvent = (eventId, data) =>
  apiRequest(`/api/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

/**
 * Обновить статус регистрации участника (посещаемость / оплата)
 * PATCH /api/events/registrations/{registrationId}/status
 */
export const patchRegistrationStatus = (registrationId, data) =>
  apiRequest(`/api/events/registrations/${registrationId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

/**
 * Получить список чатов, доступных текущему пользователю для публикации событий,
 * а также defaultChatId из шаблона пользователя.
 * GET /api/chats/accessible
 * Поддерживает оба режима аутентификации: init data (обычный) и userId+hash (inline).
 * @returns {Promise<{ defaultChatId: number|null, chats: Array<{telegramChatId: number, title: string, chatType: string}> }>}
 */
export const getAccessibleChats = (userId = null, hash = null) => {
  const params = userId && hash ? `?userId=${userId}&hash=${hash}` : '';
  return apiRequest(`/api/chats/accessible${params}`);
};

/**
 * Администратор регистрирует участника
 * POST /api/events/{eventId}/participants/register
 * body: { organizationMemberId } | { userId } | { manualName }
 */
export const registerParticipant = (eventId, data) =>
  apiRequest(`/api/events/${eventId}/participants/register`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * Пользователь записывает САМ СЕБЯ на событие (дубль логики телеграм-бота).
 * POST /api/events/{eventId}/participants/register-self
 * @returns {Promise<EventResponse>} обновлённое событие (registrations + selfRegistrationStatus)
 */
export const registerSelf = (eventId) =>
  apiRequest(`/api/events/${eventId}/participants/register-self`, {
    method: 'POST',
  });

/**
 * Пользователь снимает САМ СЕБЯ с события.
 * DELETE /api/events/{eventId}/participants/unregister-self
 * @returns {Promise<EventResponse>} обновлённое событие
 */
export const unregisterSelf = (eventId) =>
  apiRequest(`/api/events/${eventId}/participants/unregister-self`, {
    method: 'DELETE',
  });

/**
 * Предпросмотр массового добавления (dry-run) — ничего не пишет в БД.
 * POST /api/events/{eventId}/participants/resolve
 * body: { entries: string[] }  → { items: [{ rawInput, displayName, organizationMemberId, userId, guest, status }] }
 */
export const resolveParticipants = (eventId, entries) =>
  apiRequest(`/api/events/${eventId}/participants/resolve`, {
    method: 'POST',
    body: JSON.stringify({ entries }),
  });

/**
 * Массовое добавление участников списком.
 * POST /api/events/{eventId}/participants/register-batch
 * body: { entries: string[] }  → { added, alreadyRegistered, skipped, items: [...] }
 */
export const registerBatch = (eventId, entries) =>
  apiRequest(`/api/events/${eventId}/participants/register-batch`, {
    method: 'POST',
    body: JSON.stringify({ entries }),
  });

/**
 * Администратор снимает участника с регистрации
 * DELETE /api/events/{eventId}/participants/unregister
 * body: { userId }
 */
export const unregisterParticipant = (eventId, data) =>
  apiRequest(`/api/events/${eventId}/participants/unregister`, {
    method: 'DELETE',
    body: JSON.stringify(data),
  });

/**
 * Проверить, было ли событие опубликовано.
 * GET /api/events/{publicEventId}/publications/exists
 *
 * chatId не передан  → глобальная проверка (хоть куда-нибудь)
 * chatId = число     → per-chat проверка для этого чата
 * chatId = 0         → личный чат пользователя (бэкенд резолвит через auth)
 *
 * @param {string} publicEventId
 * @param {number|null} [chatId]  — undefined/null = глобально; 0 = личный чат; >0 = группа
 * @returns {Promise<{exists: boolean}>}
 */
export const checkEventPublicationExists = (publicEventId, chatId) => {
  const params = chatId !== undefined && chatId !== null
    ? `?chatId=${chatId}`
    : '';
  return apiRequest(`/api/events/${publicEventId}/publications/exists${params}`);
};

/**
 * Опубликовать событие в указанный чат.
 * POST /api/events/{publicEventId}/publish
 * body: { chatId } — null = личный чат с ботом
 */
export const publishEventToChat = (publicEventId, chatId) =>
  apiRequest(`/api/events/${publicEventId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ chatId: chatId ?? null }),
  });